import { useRef, useEffect, useState } from 'react';
import { useAsync, useUpdateEffect } from 'react-use';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import Button from 'components/Button';
import Card from 'components/Card/Card';
import Cols from 'components/Layout/Cols';
import { useFlashTransition } from 'hooks/useFlashState';
import {
  StyledFormInput,
  StyledLinkButton,
  StyledMfaWrapper,
  StyledNotice,
} from './styles';
import { StyledSubSection } from '../styles';
import { KizenTypography } from 'app/typography';
import InputControl from 'components/Inputs/InputControl';
import AuthenticationService from 'services/AuthenticationService';
import { toastVariant, useToast } from 'components/ToastProvider';
import { getErrorMessage } from 'hooks/useErrors';
import { getOriginalError } from 'services/AxiosService';
import BasicModal from 'components/Modals/presets/BasicModal';
import TextInput from 'components/Inputs/TextInput';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import WholeNumberTextInput from 'components/Inputs/TextInput/presets/WholeNumber';

const StyledCard = styled(Card)`
  max-width: 100%;
`;

const TotpSetup = ({ mfaSetting }) => {
  const { t } = useTranslation();
  const [showToast] = useToast();

  const [loading, setLoading] = useState(false);
  const [provisioningData, setProvisioningData] = useState({
    status: mfaSetting?.totp.status,
  });
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [code, setCode] = useState('');

  useAsync(async () => {
    const data = await AuthenticationService.getTOTPSetupStatus();
    setProvisioningData(data);
  }, []);

  const handleInitiateSetup = async () => {
    setLoading(true);
    try {
      const { secret, provisioning_uri, message } =
        await AuthenticationService.initiateTOTPSetup();
      setProvisioningData({
        provisioning_uri,
        secret,
        message,
        status: 'pending',
      });
      if (message) {
        showToast({
          message,
          variant: toastVariant.SUCCESS,
        });
      }
      setShowSetupModal(true);
    } catch (error) {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('Failed to configure MFA'),
        variant: toastVariant.FAILURE,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleHideSetup = () => {
    setShowSetupModal(false);
    setCode('');
  };

  const handleConfirmCode = async () => {
    setLoading(true);
    try {
      const data = await AuthenticationService.verifyTOTPSetup(code);
      setProvisioningData((prev) => ({ ...prev, status: 'enabled' }));
      showToast({
        message: data.detail || t('MFA configured successfully'),
        variant: toastVariant.SUCCESS,
      });
      handleHideSetup();
    } catch (error) {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('Failed to configure MFA'),
        variant: toastVariant.FAILURE,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleShowDeleteConfirmation = () => {
    setShowDeleteModal(true);
  };

  const handleHideDeleteConfirmation = () => {
    setShowDeleteModal(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await AuthenticationService.deleteTOTPSetup();
      setProvisioningData({ status: 'disabled' });
      showToast({
        message: t('MFA deleted successfully'),
        variant: toastVariant.SUCCESS,
      });
      setShowDeleteModal(false);
    } catch (error) {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) || t('Failed to delete MFA'),
        variant: toastVariant.FAILURE,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <KizenTypography type="subheader" margin="md">
        {t('Multi-Factor Authentication (MFA) App')}
      </KizenTypography>
      <StyledMfaWrapper>
        {provisioningData.status !== 'enabled' ? (
          <StyledLinkButton
            noSpace
            variant="text"
            color="blue"
            onClick={handleInitiateSetup}
            disabled={loading}
          >
            {t('Configure Authenticator App')}
          </StyledLinkButton>
        ) : (
          <div>
            <StyledNotice>
              {t('Success! Your MFA has been configured.')}
            </StyledNotice>
            <StyledLinkButton
              noSpace
              variant="text"
              color="red"
              onClick={handleShowDeleteConfirmation}
              disabled={loading}
            >
              {t('Delete MFA')}
            </StyledLinkButton>
          </div>
        )}
      </StyledMfaWrapper>
      {showSetupModal ? (
        <BasicModal
          heading={t('Authenticator App Setup')}
          onHide={handleHideSetup}
          onConfirm={handleConfirmCode}
          show
          size="small"
          disabled={loading || code.length !== 6}
        >
          <InputControl label={t('Scan code')} margin>
            {provisioningData.provisioning_uri ? (
              <QRCode
                size={256}
                style={{
                  width: '100%',
                }}
                value={provisioningData.provisioning_uri}
              />
            ) : null}
          </InputControl>
          <TextInput
            label={t('Secret')}
            value={provisioningData.secret}
            readOnly
            margin
          />
          <WholeNumberTextInput
            label={t('Verification Code')}
            placeholder={t('Enter Verification Code')}
            value={code}
            onChange={setCode}
            thousandSeparator={false}
          />
        </BasicModal>
      ) : null}
      {showDeleteModal ? (
        <ConfirmationModal
          onHide={handleHideDeleteConfirmation}
          onConfirm={handleDelete}
          show
          size="small"
          disabled={loading}
        >
          {t(
            "App based MFA will be deleted, and you'll email instead. Are you sure?"
          )}
        </ConfirmationModal>
      ) : null}
    </>
  );
};

const UpdatePasswordForm = ({
  values,
  onFieldChange,
  onSubmit,
  formError,
  mfaSetting,
}) => {
  const [
    currentPasswordError,
    showCurrentPasswordError,
    flashCurrentPasswordError,
  ] = useFlashTransition();
  const currentPasswordRef = useRef();

  const { t } = useTranslation();

  useUpdateEffect(() => {
    if (!currentPasswordError && currentPasswordRef.current) {
      // TODO this functionality appears not to be working
      currentPasswordRef.current.focus();
    }
  }, [currentPasswordError]);

  const [passwordError, showPasswordError, flashPasswordError] =
    useFlashTransition();
  const passwordRef = useRef();

  useUpdateEffect(() => {
    if (!passwordError && passwordRef.current) {
      // TODO this functionality appears not to be working
      passwordRef.current.focus();
    }
  }, [passwordError]);

  useEffect(() => {
    if (formError.current_password) {
      flashCurrentPasswordError(formError.current_password);
    }

    if (formError.password) {
      flashPasswordError(formError.password);
    }
  }, [formError, flashCurrentPasswordError, flashPasswordError]);

  return (
    <StyledCard>
      <StyledSubSection
        title={t('Update Password')}
        action={
          <Button noSpace disabled={false} onClick={onSubmit}>
            {t('Update')}
          </Button>
        }
      >
        <Cols columns={2} gutter="20px">
          <StyledFormInput
            ref={currentPasswordRef}
            type="password"
            label={t('Current Password')}
            autoComplete="new-password"
            placeholder={t('Enter Current Password')}
            value={values.current_password}
            onChange={(value) => onFieldChange('current_password', value)}
            className="first-row"
            margin
            validate={{
              message: currentPasswordError,
              showMessage: showCurrentPasswordError,
            }}
          />
          <StyledFormInput
            ref={passwordRef}
            type="password"
            label={t('New Password')}
            placeholder={t('Enter New Password')}
            value={values.password}
            onChange={(value) => onFieldChange('password', value)}
            className="first-row"
            required
            margin
            validate={{
              message: passwordError,
              showMessage: showPasswordError,
            }}
          />
        </Cols>
        <TotpSetup mfaSetting={mfaSetting} />
      </StyledSubSection>
    </StyledCard>
  );
};

export default UpdatePasswordForm;
